/**
 * Minimal stand-in for `react-i18next`.
 *
 * Two of the design-system components copied from 8loop (`ErrorState`,
 * `PaginationBar`) call `useTranslation()`. This app is English-only and pulling
 * in i18next for six strings would be silly, so `react-i18next` is aliased to
 * this module (see vite.config.ts / tsconfig.app.json) and the copied components
 * stay byte-identical to upstream.
 *
 * If this app ever needs real translations, delete the alias and install
 * react-i18next — nothing else has to change.
 */

const STRINGS: Readonly<Record<string, string>> = {
  "common.tryAgain": "Try again",
  "common.previousPage": "Previous page",
  "common.nextPage": "Next page",
  "common.perPage": "{{count}} / page",
  "common.showingRange": "Showing {{start}} – {{end}} of {{total}}",
  "common.showingRange_capped": "Showing {{start}} – {{end}} of {{total}}+",
};

type Options = Record<string, string | number | undefined> & {
  defaultValue?: string;
};

function interpolate(template: string, vars: Options): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => {
    const value = vars[name];
    return value === undefined ? "" : String(value);
  });
}

/** i18next's `t`: a key, plus either a default string or an options bag. */
function t(key: string, second?: string | Options): string {
  const opts: Options = typeof second === "object" && second !== null ? second : {};
  const fallback =
    typeof second === "string" ? second : (opts.defaultValue ?? key);
  const template = STRINGS[key] ?? fallback;
  return interpolate(template, opts);
}

export function useTranslation(): { t: typeof t } {
  return { t };
}
